const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
const MAX_SOURCE_SIZE = 12 * 1024 * 1024;
const MAX_OUTPUT_SIZE = 2.7 * 1024 * 1024;

interface DecodedPhoto {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

async function decodePhoto(file: File): Promise<DecodedPhoto> {
  if ("createImageBitmap" in window) {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close(),
      };
    } catch {
      // Safari sait parfois afficher une photo HEIC sans que createImageBitmap
      // puisse la décoder. Le repli HTMLImageElement couvre ce cas.
    }
  }

  const objectUrl = URL.createObjectURL(file);
  const image = new Image();
  image.decoding = "async";
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("Cette photo ne peut pas être ouverte sur cet appareil."));
      image.src = objectUrl;
    });
    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      release: () => URL.revokeObjectURL(objectUrl),
    };
  } catch (error) {
    URL.revokeObjectURL(objectUrl);
    throw error;
  }
}

function canvasBlob(canvas: HTMLCanvasElement, type: string, quality: number) {
  return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));
}

export async function compressRecipePhoto(file: File): Promise<File> {
  const extensionLooksValid = /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);
  if ((!file.type || !ACCEPTED_TYPES.includes(file.type.toLowerCase())) && !extensionLooksValid) {
    throw new Error("Choisissez une image JPEG, PNG, WebP ou HEIC.");
  }
  if (file.size > MAX_SOURCE_SIZE) {
    throw new Error("La photo dépasse 12 Mo.");
  }

  const decoded = await decodePhoto(file);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    decoded.release();
    throw new Error("Impossible de préparer cette photo.");
  }

  try {
    for (const maxDimension of [1600, 1280, 1024]) {
      const scale = Math.min(1, maxDimension / Math.max(decoded.width, decoded.height));
      canvas.width = Math.max(1, Math.round(decoded.width * scale));
      canvas.height = Math.max(1, Math.round(decoded.height * scale));
      context.fillStyle = "#fffdf7";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(decoded.source, 0, 0, canvas.width, canvas.height);

      for (const [type, quality] of [
        ["image/webp", 0.8],
        ["image/webp", 0.65],
        ["image/jpeg", 0.82],
        ["image/jpeg", 0.68],
      ] as const) {
        const blob = await canvasBlob(canvas, type, quality);
        if (!blob || blob.type !== type || blob.size > MAX_OUTPUT_SIZE) continue;
        const extension = type === "image/webp" ? "webp" : "jpg";
        return new File([blob], `${file.name.replace(/\.[^.]+$/, "")}.${extension}`, { type });
      }
    }
  } finally {
    decoded.release();
  }

  throw new Error("La photo reste trop volumineuse après compression. Essayez une autre image.");
}
